import React from 'react';
import { useTranslation } from 'react-i18next';
import { MeterChart } from '@carbon/charts-react';
import { OrderTypeEnum } from '@restavo/shared/enums/order';
import { useMoney } from 'src/hooks/useMoney';
import { getOrderTypeText } from 'src/utils/order';
import { useChartTheme } from './useChartTheme';

/** One row of `salesReports.salesReportsByType` — `SUM(Orders.grandTotal)` grouped by order type. */
export interface SalesByTypeRow {
  orderType: number;
  totalSales: number;
}

/** Shown even at zero, so the legend does not shift as the window changes. */
const METER_ORDER_TYPES = [
  OrderTypeEnum.DINE_IN,
  OrderTypeEnum.TAKEAWAY,
  OrderTypeEnum.DELIVERY,
  OrderTypeEnum.OTHER,
] as const;

/**
 * The meter's segments, summing to exactly the Total Sales tile.
 *
 * Both figures come from the same `SUM(Orders.grandTotal)`, so consuming EVERY row of
 * `salesReportsByType` — rather than naming the types by hand — is what keeps them equal. The
 * meter used to disagree in both directions at once: it added `foodAppsSalesReports`, which the
 * server builds from the `Transactions` table and not from orders at all, and it left out
 * `OrderTypeEnum.OTHER`, which the tile counts.
 *
 * Any order type outside the enum folds into OTHER rather than becoming its own segment —
 * `getOrderTypeText` already labels it "other", so a separate group would duplicate that legend
 * entry while adding nothing readable.
 */
export function buildSalesGoalGroups(rows: SalesByTypeRow[] = []): SalesByTypeRow[] {
  const totals = new Map<number, number>(METER_ORDER_TYPES.map((type) => [type, 0]));
  for (const row of rows) {
    const orderType = Number(row?.orderType);
    const group = totals.has(orderType) ? orderType : OrderTypeEnum.OTHER;
    totals.set(group, (totals.get(group) ?? 0) + (Number(row?.totalSales) || 0));
  }
  return [...totals.entries()].map(([orderType, totalSales]) => ({ orderType, totalSales }));
}

interface SalesGoalProps {
  title: string;
  isLoading: boolean;
  salesReportsByType: SalesByTypeRow[];
  goal: number;
}

const SalesGoal = React.memo((props: SalesGoalProps) => {
  const { t } = useTranslation();
  const chartTheme = useChartTheme();
  const { formatCompact } = useMoney();
  return (
    <div className="dashboard__stat_widget">
      <MeterChart
        data={buildSalesGoalGroups(props.salesReportsByType).map((row) => ({
          group: t(getOrderTypeText(row.orderType)),
          value: row.totalSales,
        }))}
        options={{
          title: props.title,
          height: '130px',
          data: { loading: props.isLoading },
          meter: {
            proportional: {
              total: props.goal,
              breakdownFormatter: (value) =>
                t('dashboard:currentSales', {
                  datasetsTotal: formatCompact(value.datasetsTotal),
                  total: formatCompact(value.total),
                }),
              totalFormatter: (value) => t('dashboard:goal', { value: formatCompact(value) }),
            } as any,
          },
          animations: true,
          theme: chartTheme,
        }}
      />
    </div>
  );
});

export default SalesGoal;
